import { X, AlertTriangle } from 'lucide-react';
import { usePlant } from '../../../context/PlantContext';
import { getPlantImageUrl } from '../../../utils/plantImageUtils';
import { useHasCoarsePointer } from '../../../hooks/useHasCoarsePointer';

/**
 * CompactPlantCard - Horizontal plant card for favorites list
 *
 * Displays plant information in a compact 280×80px card with image,
 * name, scientific name, height, toxic warning, and action buttons.
 *
 * @param {Object} props - Component props
 * @param {Object} props.plant - Plant object with all properties
 * @param {Function} props.onRemove - Callback when remove button clicked (receives plant.id)
 * @param {Function} props.onPlace - Callback when place button clicked (receives plant object)
 */
const CompactPlantCard = ({ plant, onRemove, onPlace }) => {
  const { enterPlacementMode } = usePlant();
  // On touch (iPad/mobile) HTML5 drag-and-drop never fires a drop, so the drag
  // affordance is a dead end. There the "Platzieren" tap flow is the only working
  // path — drop `draggable`/`grab` and promote the button to a touch target (#748).
  const isTouch = useHasCoarsePointer();

  if (!plant) {
    return null;
  }

  // Extract plant properties
  const {
    id,
    name,           // API uses 'name', not 'german_name'
    german_name,    // Fallback for compatibility
    scientific_name,
    properties = {}
  } = plant;

  // Support both API format (min_height_cm) and legacy format (height_min)
  const {
    min_height_cm,
    max_height_cm,
    height_min,    // Legacy format
    height_max,    // Legacy format
    toxic_to_pets,
    toxic_to_children,
    growth_type
  } = properties;

  // Use API format first, fallback to legacy
  const heightMin = min_height_cm || height_min;
  const heightMax = max_height_cm || height_max;
  const displayName = german_name || name;
  const imageName = name || german_name; // DB 'name' matches image filename

  // Determine if plant is toxic
  const isToxic = toxic_to_pets || toxic_to_children;
  const isLawn = growth_type === 'lawn';

  // Format height display - only show if we have height data
  const heightDisplay = (() => {
    if (heightMin && heightMax) {
      return `${heightMin} - ${heightMax} cm`;
    } else if (heightMin) {
      return `ab ${heightMin} cm`;
    } else if (heightMax) {
      return `bis ${heightMax} cm`;
    }
    return null; // Don't show if no height data
  })();

  /**
   * Handle drag start - set plant data for drop handler
   */
  const handleDragStart = (e) => {
    // Set plant data as JSON in dataTransfer
    e.dataTransfer.setData('application/json', JSON.stringify(plant));
    e.dataTransfer.effectAllowed = 'copy';

    // Add dragging class for visual feedback (semi-transparent)
    e.currentTarget.style.opacity = '0.5';
  };

  /**
   * Handle drag end - restore opacity
   */
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };

  return (
    <div
      draggable={!isTouch}
      onDragStart={isTouch ? undefined : handleDragStart}
      onDragEnd={isTouch ? undefined : handleDragEnd}
      className="flex items-center gap-3 w-[280px] h-[80px] bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors duration-200"
      data-testid="compact-plant-card"
      style={{ cursor: isTouch ? 'default' : 'grab' }}
    >
      {/* Plant Image - 60×60px */}
      <div className="flex-shrink-0 w-[60px] h-[60px] bg-gray-100 rounded-lg overflow-hidden">
        {imageName ? (
          <img
            src={getPlantImageUrl(imageName, 'thumb')}
            alt={displayName || imageName}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-gray-400 text-xs">Kein Bild</span>
          </div>
        )}
      </div>

      {/* Plant Info */}
      <div className="flex-1 min-w-0">
        {/* Plant Name */}
        <h3 className="text-sm font-semibold text-gray-900 truncate" title={displayName}>
          {displayName || 'Unbekannte Pflanze'}
        </h3>

        {/* Scientific Name */}
        {scientific_name && (
          <p className="text-xs italic text-gray-600 truncate" title={scientific_name}>
            {scientific_name}
          </p>
        )}

        {/* Height - Only show if we have data */}
        {heightDisplay && (
          <p className="text-xs text-gray-500 mt-0.5">
            {heightDisplay}
          </p>
        )}

        {/* Toxic Warning Badge */}
        {isToxic && (
          <div className="flex items-center gap-1 mt-1">
            <AlertTriangle className="w-3 h-3 text-red-600" />
            <span className="text-xs text-red-600 font-medium">Giftig</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-1 flex-shrink-0">
        {/* Remove Button */}
        <button
          onClick={() => onRemove(id)}
          onMouseDown={(e) => e.stopPropagation()} // Prevent dragging when clicking button
          draggable={false}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 transition-colors duration-200 group"
          aria-label="Aus Favoriten entfernen"
          title="Aus Favoriten entfernen"
        >
          <X className="w-4 h-4 text-gray-500 group-hover:text-red-600" />
        </button>

        {/* Place Button – hidden for lawn types (not individually placeable) */}
        {!isLawn && (
          <button
            onClick={() => {
              // Enter placement mode (triggers PlantPlacementManager)
              enterPlacementMode(plant);

              // Also call parent callback if provided
              if (onPlace) {
                onPlace(plant);
              }
            }}
            onMouseDown={(e) => e.stopPropagation()} // Prevent dragging when clicking button
            draggable={false}
            className={`bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded transition-colors duration-200 ${
              isTouch ? 'px-3 py-2 text-sm min-h-[40px]' : 'px-2 py-1 text-xs'
            }`}
            aria-label="Pflanze platzieren"
            title="Pflanze platzieren"
          >
            Platzieren
          </button>
        )}
      </div>
    </div>
  );
};

export default CompactPlantCard;
